import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../../constants/api';

class HttpClient {
  private instance: AxiosInstance;
  private tokenRefreshPromise: Promise<void> | null = null;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
    this.startTokenRefreshTimer();
  }

  private redirectToLogin() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    window.location.href = '/login';
  }

  private setupInterceptors() {
    // Request interceptor para agregar token de autenticación
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Let the browser set Content-Type for FormData (includes multipart boundary)
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor para manejo de errores globales y desenvolver respuestas
    this.instance.interceptors.response.use(
      (response) => {
        // Desenvolver respuestas del backend si tienen el formato envelope
        // { data: {...}, statusCode: number, timestamp: string }
        if (
          response.data &&
          typeof response.data === 'object' &&
          'data' in response.data &&
          'statusCode' in response.data &&
          'timestamp' in response.data
        ) {
          // Reemplazar la data de la respuesta con la data interna
          response.data = response.data.data;
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Si el token expiró, intentar refrescarlo
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              // Llamar al endpoint de refresh para obtener nuevos tokens
              const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {
                refreshToken
              });

              // El backend envuelve la respuesta en { data, statusCode, timestamp }
              // Como usamos axios puro (no this.instance), debemos desenvolver manualmente
              const responseData = refreshResponse.data?.data ?? refreshResponse.data;
              const { accessToken, refreshToken: newRefreshToken } = responseData;
              
              // Guardar los nuevos tokens
              localStorage.setItem('accessToken', accessToken);
              localStorage.setItem('refreshToken', newRefreshToken);
              
              // Actualizar el header del request original
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              
              // Reintentar la petición original
              return this.instance(originalRequest);
            } else {
              // No hay refresh token, redirigir al login
              this.redirectToLogin();
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            // Si no se puede refrescar, limpiar tokens y redirigir
            this.redirectToLogin();
          }
        }

        return Promise.reject(error);
      },
    );
  }

  async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config);
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data, config);
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data, config);
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.patch<T>(url, data, config);
  }

  async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url, config);
  }

  private startTokenRefreshTimer() {
    // Renovar token proactivamente cada 12 minutos (el access token expira en 15 min)
    const REFRESH_INTERVAL = 12 * 60 * 1000; // 12 minutos en milisegundos
    
    setInterval(async () => {
      await this.refreshTokenProactively();
    }, REFRESH_INTERVAL);
    
    // También renovar al detectar actividad del usuario
    this.setupActivityListener();
  }

  private setupActivityListener() {
    let lastActivity = Date.now();
    const ACTIVITY_THRESHOLD = 30 * 60 * 1000; // 30 minutos
    
    const updateActivity = () => {
      lastActivity = Date.now();
    };
    
    // Detectar actividad del usuario
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });
    
    // Verificar cada 5 minutos si hay actividad y el token necesita renovación
    setInterval(async () => {
      const timeSinceActivity = Date.now() - lastActivity;
      
      // Si hay actividad reciente, renovar token proactivamente
      if (timeSinceActivity < ACTIVITY_THRESHOLD) {
        await this.refreshTokenProactively();
      }
    }, 5 * 60 * 1000); // 5 minutos
  }

  private async refreshTokenProactively(): Promise<void> {
    // Evitar múltiples renovaciones simultáneas
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return;

    this.tokenRefreshPromise = this.performTokenRefresh();
    
    try {
      await this.tokenRefreshPromise;
    } finally {
      this.tokenRefreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      
      const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refreshToken
      });

      // El backend envuelve la respuesta en { data, statusCode, timestamp }
      // Como usamos axios puro (no this.instance), debemos desenvolver manualmente
      const responseData = refreshResponse.data?.data ?? refreshResponse.data;
      const { accessToken, refreshToken: newRefreshToken } = responseData;
      
      // Guardar los nuevos tokens
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      
    } catch (error) {
      console.warn('⚠️ Error renovando token proactivamente:', error);
      // No redirigir aquí - solo fallar silenciosamente
      // La renovación reactiva (en el interceptor) manejará el error
    }
  }
}

export const httpClient = new HttpClient();

// Cliente HTTP público para endpoints que no requieren autenticación
class PublicHttpClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Solo interceptor de respuesta para desenvolver datos, sin autenticación
    this.instance.interceptors.response.use(
      (response) => {
        // Desenvolver respuestas del backend si tienen el formato envelope
        if (
          response.data &&
          typeof response.data === 'object' &&
          'data' in response.data &&
          'statusCode' in response.data &&
          'timestamp' in response.data
        ) {
          response.data = response.data.data;
        }
        return response;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
  }

  async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config);
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data, config);
  }
}

export const publicHttpClient = new PublicHttpClient();